<template>
  <div class="discount-codes">
    <div v-if="loading_dc" class="text-center">
      <loader />
    </div>
    <template v-else>
      <h5 class="discount-codes__title">Aggiungi codice sconto</h5>

      <div class="discount-codes__wrap">
        <div class="discount-codes__add">
          <div
            v-for="(dc_config_item, dc_config_index) in dc_config"
            :key="`dc_config_${dc_config_index}`"
            class="discount-codes__add-item"
          >
            <div class="discount-codes__add-logo" v-if="dc_config_item.brand">
              {{ dc_config_item.brand }}
            </div>
            <validation-observer v-slot="{ invalid }" ref="discount-codes-validation">
              <validation-provider
                rules="alpha_num|min:16|max:16"
                name="Codice sconto"
                v-slot="{ errors }"
              >
                <div class="discount-codes__add-provider" :set_invalid_flag="invalid_flag=(invalid || generic_discount_code == '')">
                <vb-input
                  class="discount-codes__add-input"
                  type="text"
                  ref="generic_discount_code"
                  v-model="generic_discount_code"
                  :vb_options="{
                    placeholder: 'es: RFdBkpTTHam8w769',
                  }"
                />
                <vb-button
                  class="discount-codes__add-btn"
                  :vb_options="{
                    variant: 'light',
                    disabled: invalid_flag
                  }"
                  @click="add_code(generic_discount_code)"
                >
                  <vb-icon
                    :name="'plus_circle'"
                    :color="sass('info')"
                    :size="22"
                  />
                </vb-button>
                </div>
                <span class="vee--errors">{{ errors[0] }}</span>
              </validation-provider>
            </validation-observer>
          </div>
        </div>
        <div class="discount-codes__items">
          <div class="discount-codes__item" v-for="(code, index) in codes_with_display_info" :key="`code_${index}`">
            <div class="discount-codes__item-data">
              <b-alert
                show
                class="discount-codes__item-alert"
                :variant="code.usable ? 'success' : 'danger'"
              >
                <vb-icon
                  class="mr-1"
                  :name="'check_circle'"
                  :color="code.usable ? '#2a7333' : '#911c20'"
                  :size="9"
                />
                <strong>{{ code.discountCode }}</strong>
              </b-alert>
              <vb-icon
                class="discount-codes__item-remove"
                :name="'remove'"
                :color="sass('dark')"
                :size="17"
                @click="remove_code(code.discountCode)"
              />
            </div>

            <div class="discount-codes__item-errors" v-if="code.display_info.show_error">
              <span>{{ get_error_message(code) }}</span>
            </div>

            <div class="discount-codes__item-copy">
              <div v-if="code.display_info.show_not_divisible">
                <span v-b-popover.hover.top="tooltip_texts.not_divisible">
                  Non frazionabile
                </span>
              </div>

              <div v-if="code.display_info.show_not_cumulable">
                <span v-b-popover.hover.top="tooltip_texts.not_cumulable">
                  Non cumulabile
                </span>
              </div>

              <template v-if="code.display_info.show_values">
                <div>
                  <span v-b-popover.hover.top="tooltip_texts.values">
                    Valore disponibile
                  </span>
                  <span>€ {{ code.display_info.value_residual }}</span>
                </div>

                <div>
                  <span v-b-popover.hover.top="tooltip_texts.values">
                    Valore residuo dopo la prenotazione
                  </span>
                  <span>€ {{ code.display_info.value_after }}</span>
                </div>
              </template>
            </div>
          </div>

        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { number } from "mathjs"
import { service_handler } from '@/services'
import {
  build_codes_string,
  add_code_to_list,
  remove_code_from_list,
  calculate_totals,
  get_code_error_type,
  get_code_display_info,
} from './logic'

const ERROR_MESSAGES = {
  'NOT FOUND': 'Non abbiamo trovato questo buono sconto. Assicurati di averlo digitato correttamente rispettando MAIUSCOLE e minuscole',
  'EXPIRED': 'Buono sconto scaduto',
  'EMPTY': 'Questo buono sconto è già stato interamente utilizzato per un\'altra prenotazione',
  'DISABLED': 'Questo buono sconto non è attivo, contatta l\'assistenza',
  'NOT CUMULABLE': 'Questo buono sconto non è cumulabile, togli gli altri codici dalla lista per utilizzare questo',
  'NOT_DIVISIBLE': 'Questo buono è di tipo non frazionabile e non può essere speso parzialmente',
  'DEFAULT': 'Impossibile usare questo buono sconto'
}

const TOOLTIPS = {
  not_divisible: 'Questo Buono può essere speso solo in modo completo e per una singola prenotazione',
  not_cumulable: 'Questo buono non può essere utilizzato congiuntamente ad altri buoni',
  values: 'In base a possibili utilizzi del Buono sconto per precedenti prenotazioni',
}

export default {
  name: "discount-codes",

  props: {
    live_price: [Number, String],
    av_from_state: [Number, String],
    dc_from_state: {
      type: Array,
      default: () => []
    },
    dc_config: {
      type: Array,
      default: () => [{ brand: false }]
    }
  },

  data() {
    return {
      loading_dc: true,
      discount_codes_amount: 0,
      generic_discount_code: '',
      discount_codes: [],
      availability: 0,
    };
  },

  computed: {
    discount_price() {
      return number(this.live_price);
    },

    codes_with_display_info() {
      return this.discount_codes.map(code => ({
        ...code,
        display_info: get_code_display_info(code)
      }))
    },

    tooltip_texts() {
      return TOOLTIPS
    },
  },

  methods: {
    validate_codes() {
      this.loading_dc = true

      if (!this.discount_codes.length) {
        this.update_totals()
        this.loading_dc = false
        return
      }

      const codes_string = build_codes_string(this.discount_codes)

      let last_response_codes = null
      service_handler('dcCheck', {
        discountCodes: codes_string,
      })
      .then((response) => {
        if (response.data.updateAvailabilityTo !== undefined) {
          this.availability = number(response.data.updateAvailabilityTo)
        }
        this.discount_codes = Object.values(response.data.discountCodes)
        this.update_totals()
        last_response_codes = response.data.discountCodes
      })
      .finally(() => {
        this.loading_dc = false
        this._track_add_outcome(last_response_codes)
      })
    },

    _track_add_outcome(discountCodes) {
      // Solo se è in corso un add (target settato da add_code).
      // Reset incondizionato del target qui (.finally del chiamante) per
      // evitare di lasciarlo "in volo" se la richiesta fallisce o si annulla.
      const target_code = this._track_add_target
      this._track_add_target = null
      if (!target_code) return
      const target = Object.values(discountCodes || {})
        .find(c => c.discountCode === target_code)
      if (!target) {
        this.track_form({
          event: 'sidebar-discount-codes:add:fail',
          data: { type: 'error', reason: 'NOT_IN_RESPONSE' },
        })
        return
      }
      const error_type = get_code_error_type(target)
      const has_error = ['NOT FOUND', 'EXPIRED', 'EMPTY', 'DISABLED', 'NOT CUMULABLE']
        .includes(target.status) || error_type === 'NOT_DIVISIBLE'
      if (has_error) {
        this.track_form({
          event: 'sidebar-discount-codes:add:fail',
          data: { type: 'error', reason: error_type },
        })
      } else {
        this.track_form({
          event: 'sidebar-discount-codes:add:success',
          data: { type: 'info' },
        })
      }
    },

    update_totals() {
      this.loading_dc = true

      const { discount_codes_amount, updated_codes } = calculate_totals({
        discount_codes: this.discount_codes,
        discount_price: this.discount_price,
        availability: this.availability
      })

      this.discount_codes = updated_codes
      this.discount_codes_amount = discount_codes_amount

      this.$emit('change', {
        name: 'discount-codes-update',
        payload: {
          discount_codes_amount: this.discount_codes_amount,
          discount_codes: this.discount_codes
        }
      })

      this.loading_dc = false
    },

    add_code(code) {
      this.discount_codes = add_code_to_list(this.discount_codes, code)
      this.generic_discount_code = ''
      this.track_form({ event: 'sidebar-discount-codes:add', data: { type: 'info' } })
      this._track_add_target = code
      this.validate_codes()
    },

    remove_code(discount_code_to_remove) {
      this.loading_dc = true
      this.discount_codes = remove_code_from_list(this.discount_codes, discount_code_to_remove)
      this.generic_discount_code = ''
      this.validate_codes()
    },

    get_error_message(code) {
      const error_type = get_code_error_type(code)
      if (error_type === 'EXPIRED') {
        return `${ERROR_MESSAGES[error_type]} il ${code.expirationDate}`
      }
      return ERROR_MESSAGES[error_type]
    },
  },

  mounted() {
    this.discount_codes = this.dc_from_state
    this.availability = this.av_from_state
    this.validate_codes()
  },
}
</script>

<style lang='scss' scoped>

  .discount-codes {
    font-size: $font-size-md;

    &__title {
      @extend %sidebar__title;
    }

    &__add {

      &-provider {
        display: flex;
      }

      &-input {
        flex-basis: 90%;
      }

      &-btn {
        flex-basis: 10%;
        padding: 0;
        margin: 0 -1px 0 $grid-gutter-width / 3;
        // todo: integrare versione solo immagine nel componente vb-button
        background: transparent !important;
        box-shadow: none !important;
        border: none !important;

        ::v-deep {
          &.disabled path {
            fill: $gray-400 !important;
          }
        }
      }
    }

    &__item {
      margin-top: $grid-gutter-width / 2;
      padding-top: $grid-gutter-20;
      border-top: 1px solid $gray-300;

      &:first-child {
        margin-top: $grid-gutter-20;
      }

      &-data {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      &-errors {
        padding-left: $grid-gutter-width / 5;
        line-height: normal;
        margin-top: $grid-gutter-width / 3;
        font-weight: $vb-font-weight-semibold;
      }

      &-alert {
        padding: 0 5px;
        margin-bottom: 0;
        display: flex;
        align-items: center;
      }

      &-remove {
        cursor: pointer
      }

      &-copy {
        padding-left: $grid-gutter-width / 5;

        div {
          margin-top: $grid-gutter-width / 7;
          display: flex;
          justify-content: space-between;

          &:first-child {
            margin-top: $grid-gutter-width / 3;
          }
        }
      }
    }
  }

</style>